import {
  Component,
  OnDestroy,
  OnInit,
  ViewChildren,
  Input,
  EventEmitter,
  Output,
  ChangeDetectorRef,
  ViewChild,
  signal,
  computed
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, Subject, forkJoin, of } from 'rxjs';
import {
  AllowedTemplateOverrides,
  ChannelType,
  ChannelUIds,
  ChannelsCascadeSettings,
  CommunicationTemplate,
  MessageSettings,
  MassNotification,
  MassNotificationTemplate,
  MassNotificationTemplateGroup,
  ToRecipientSettings,
  TemplateViewControlType,
  CommunicationTemplateViewControl,
  MassNotificationType,
  LayerAssetSummary,
  MassNotificationSeverity,
  ConfirmationButton,
  MassNotificationItemSource,
  MassRecipientType,
  RelatedAssetRecipientSettings,
  NotificationDocumentRecipientSettings
} from 'src/app/modules/communications/store/communications/communications.model';
import { Dataset } from 'src/app/modules/core/store/models/dataset.model';
import * as fromComm from 'src/app/modules/communications/store/communications';
import { Store } from '@ngrx/store';
import { catchError, map, take, takeUntil, withLatestFrom } from 'rxjs/operators';
import { cloneDeep } from 'lodash-es';
import { GUID_EMPTY } from '@app/core/constants';
import { Document, QueryParams, Workflow, WorkflowStep, WorkflowStepAction, WorkflowStepTypes } from 'src/app/modules/core/store/models';
import { CommunicationsApiService } from '../../../store/communications/communications-api.service';
import { TemplateDesignerService } from '../../../services/template-designer.service';
import { UserProfile } from 'src/app/modules/core/store/user-profile/user-profile.model';
import { CultureService } from 'src/app/modules/core/services/culture.service';
import { ChannelSelectionService } from '../../../services/channel-selection-service';
import { WorkflowApiService } from '../../../store/workflow/workflow-api.service';
import moment from 'moment';
import produce from 'immer';
import { QuillEditorService } from 'src/app/modules/core/services/quill/quill.service';
import { TextValueByCulture } from 'src/app/modules/core/store/models/translations.model';
import { UserProfileApiService } from 'src/app/modules/core/store/user-profile';
import { UserContextService } from 'src/app/modules/core/services/user-context.service';
import { FieldToValueMapping, SelectedElementEvent } from '../../../components/template/designer/design-elements/design-elements.models';
import { ClosestAssetFilterDto, DocumentSummaryByLayer, DocumentSummaryWithEmail } from 'src/app/modules/cop/store/impacted-assets';
import { AssetsElementSettings } from '../../../components/template/designer/design-elements/controls/assets-control/assets-control-model';
import { TemplateDesigner } from '../../../components/template/designer/designer/designer.component';
import { ScreenshotService } from 'src/app/modules/core/services/screenshot.service';
import { DatasetApiService } from 'src/app/modules/core/services/dataset-api.service';
import { DataApiService } from 'src/app/modules/management/store/data/data-api.service';
import { MapElementSettings } from '../../../components/template/designer/design-elements/controls/map-control/map-control-model';
import { NotificationStatusService } from '../../../services/notification-status-service';
import { DatasetService } from '@app/modules/core/services/dataset.service';
import { RecipientSelectionService } from '@app/modules/communications/services/recipient-selection-service';
import {
  ClosestAssetCountByLayerEx,
  RecipientAssetsSelectionComponent
} from '@app/modules/communications/components/recipient-assets-selection/recipient-assets-selection.component';
import { RatingScaleLevel } from '@app/modules/management/store/ratings/ratings.model';
import { TemplateSelectItem } from './template-select-expansion-panel/template-select-expansion-panel.component';
import { forEach } from 'jszip';
import { ConfirmationElementSettings } from '@app/modules/communications/components/template/designer/design-elements/controls/confirmation-control/confirmation-control-model';

enum TemplateSelectDisplayType {
  TemplateType = 0,
  TemplateGroup = 1
}

interface TemplateSelectGroup {
  id: string;
  label: string;
  templates: TemplateSelectItem[];
}

@Component({
  selector: 'topo-comms-new-notification',
  templateUrl: './new-notification.component.html',
  styleUrls: ['./new-notification.component.scss'],
  standalone: false
})
export class NewNotificationComponent implements OnInit, OnDestroy {
  @Input() closestAssetRequest: ClosestAssetFilterDto;
  @Input() isIncident: boolean;

  _dataset: Dataset;
  totalRelatedAssetRecipients: number;
  @Input() set dataset(value: Dataset) {
    this._dataset = value;
    this.documentContext = { dataset: this._dataset, document: this._document };
  }

  _document: Document;
  @Input() set document(value: Document) {
    this.inMapContext = true;
    this._document = value;
    this.documentContext = { dataset: this._dataset, document: this._document };
  }

  @Input() mapUId: string;

  assetCountsByLayer$: BehaviorSubject<any> = new BehaviorSubject(null);
  @Input() set assetCountsByLayer(value: any) {
    this.assetCountsByLayer$.next(value);
  }

  @Output() loadClosestAssets: EventEmitter<string> = new EventEmitter();
  @Output() isActiveChanged: EventEmitter<boolean> = new EventEmitter();

  @ViewChildren(TemplateDesigner) designers;
  @ViewChild('assetsSelection') assetsSelection: RecipientAssetsSelectionComponent;

  isActive: boolean;
  isDirty: boolean;
  inMapContext: boolean;
  notification: MassNotification;
  notificationToSave: MassNotification;
  templates = signal<MassNotificationTemplate[]>([]);
  templateGroups = signal<MassNotificationTemplateGroup[]>([]);
  selectedTemplate: MassNotificationTemplate;
  selectedTemplateUId: string;
  selectedMessage: string;
  selectedSMSMessage: string;
  workflow: Workflow;
  userProfile: UserProfile;
  channelTypes: ChannelType[];
  selectedCulture: string;
  languages: string[];
  skippedChannels: string[];
  showConfirmValidation: boolean;
  showChangeTemplateConfirmation: boolean;
  potentialTemplateUId: string;
  templateFilter = signal<string>(null);

  templateSubjects: { [uid: string]: string } = {};
  templateSummary: { [uid: string]: string } = {};
  documentMappings: FieldToValueMapping;

  layerAssetSummaryItems: LayerAssetSummary[];
  loadedAssetsForTemplate: any[];
  loadedAssetContacts: DocumentSummaryWithEmail[] = [];
  assetContactsForDisplay: DocumentSummaryWithEmail[] = [];

  designExpanded: boolean;

  canEditMessage: boolean;
  canSelectTemplate: boolean;

  hasHTMLMessage: boolean;
  hasSMSMessage: boolean;
  needToApplyOverrides: boolean;
  hasConfirmation: boolean;
  singleSmsLimit: any = 277;
  absoluteMaxLimit: any = 277;

  totalRecipients: number;
  totalAddedRecipients: number;
  totalAssetRecipients: number;
  editor: any;
  editableControlTypes: TemplateViewControlType[] = [TemplateViewControlType.Text, TemplateViewControlType.Image];
  deletableControlTypes: TemplateViewControlType[] = [TemplateViewControlType.Map, TemplateViewControlType.Image];
  validationList: string[];
  destroy$ = new Subject<void>();
  allAllowedChangesForChannels: AllowedTemplateOverrides = AllowedTemplateOverrides.Channels;

  mapImage: any;
  isConfirmWarning: boolean;
  hasTranslations: boolean;
  limitByLanguage: boolean;

  selectedTabIndex: number = 0;
  tabs: string[] = [];
  disabledTabs: number[] = [];

  totalChannels: number;
  hasChannels: boolean;
  GUID_EMPTY = GUID_EMPTY;
  buttonWorkflowActions: { name: string; destinationStepUId: string; uId: string }[];

  documentContext: { dataset: Dataset; document: Document };

  assetsRelatedCounts: { [uid: string]: number } = {};
  distMemberContacts: { [uid: string]: number };

  ratingScaleLevels?: RatingScaleLevel[];
  selectedSeverity: MassNotificationSeverity;
  notificationForTimeline: MassNotification;

  notificationDocumentRecipientCount: number;
  relatedAssetRecipientCount: number;
  messageCharCount: number;
  smsMessageCharCount: number;
  availableChannelUIds: string[] = [];
  notificationName: string;
  contentTabs: string[] = [];
  contentTabIcons: string[] = [];
  contentTabTemplates: CommunicationTemplate[] = [];
  selectedContentTabIndex: number = 0;
  contentTemplate: CommunicationTemplate;
  singleMessageMode: boolean = true;

  showSendConfirmation: boolean;
  sendConfirmationTitle: string;
  sendConfirmationMessage: string;
  channelMessageList: string[] = [];
  skipTextUpdate: boolean;

  isDesignerSmsOverLimitReached: boolean = false;
  warningThreshold: any = 161;
  isSmsOverLimit: boolean;
  readonly MAX_CHARS = 277;
  isDesignerSmsOverLimitReached$ = new BehaviorSubject<boolean>(false);
  designerSmsCount: number;
  blockInput: boolean = true;
  progressPercentage = 0;
  showWarning = false;
  smsCount: number;

  // Add missing property that's referenced in the HTML template
  includeAssetsRecipients: boolean = false;

  templateSelectDisplayType = signal<TemplateSelectDisplayType>(TemplateSelectDisplayType.TemplateType); // display template selection grouped by template type or template group

  validTemplateFilter = computed(() => {
    const value = this.templateFilter();
    return !!value?.trim() && value.length > 2 ? value : null;
  });

  filteredTemplates = computed(() => {
    const templates = this.templates() ?? [];
    const keywordFilter = this.validTemplateFilter();

    if (!!keywordFilter) {
      return templates.filter(({ name, templateGroupUId, templateGroupName }) => {
        const _keyword = keywordFilter.toLocaleLowerCase();
        return (
          name?.toLocaleLowerCase()?.includes(_keyword) ||
          (!!templateGroupUId && templateGroupName?.toLocaleLowerCase()?.includes(_keyword))
        );
      });
    }

    return templates;
  });
  groupedTemplatesForSelect = computed<TemplateSelectGroup[]>(() => {
    const templates = this.filteredTemplates();
    const displayType = this.templateSelectDisplayType() ?? TemplateSelectDisplayType.TemplateType;

    switch (displayType) {
      case TemplateSelectDisplayType.TemplateGroup:
        const groups = this.templateGroups();

        const templatesByGroup = templates.reduce(
          (acc, t) => {
            const groupId = t?.templateGroupUId ?? 'unassigned';

            if (!acc[groupId]) {
              acc[groupId] = [];
            }

            acc[groupId].push({
              uId: t.uId,
              name: t?.name,
              badgeText: this.translateService.instant(`COMMUNICATIONS.NOTIFICATION-TEMPLATES-TYPE-${t.type}-SHORT`)
            });

            return acc;
          },
          {
            unassigned: []
          } as Record<string, TemplateSelectItem[]>
        );

        const templateGroupMap = groups.reduce(
          (g_acc, g) => ({
            ...g_acc,
            [g.uId]: {
              id: g.uId,
              label: g?.name,
              templates: (templatesByGroup[g.uId] ?? []).sort((a, b) => a.name.localeCompare(b.name))
            }
          }),
          {
            unassigned: {
              id: 'unassigned',
              label: this.translateService.instant('COMMUNICATIONS.UNASSIGNED-TEMPLATES'),
              templates: templatesByGroup.unassigned.sort((a, b) => a.name.localeCompare(b.name))
            }
          } as Record<string, TemplateSelectGroup>
        );

        const sortedTemplateGroups = (Object.values(templateGroupMap) as TemplateSelectGroup[]).sort((a, b) => {
          if (a.id === 'unassigned') {
            return 1;
          } else if (b.id === 'unassigned') {
            return -1;
          }

          return a.label.localeCompare(b.label);
        });

        const keywordFilter = this.validTemplateFilter();

        if (!keywordFilter) {
          return sortedTemplateGroups;
        }

        return sortedTemplateGroups.filter(g => g.templates.length || g.label.toLocaleLowerCase().includes(keywordFilter));

      case TemplateSelectDisplayType.TemplateType:
      default:
        const templatesByTypeMap = templates.reduce(
          (acc, t) => {
            if (!acc[t.type]) {
              acc[t.type] = [];
            }

            acc[t.type].push(t);

            return acc;
          },
          {} as Record<MassNotificationType, MassNotificationTemplate[]>
        );

        return Object.entries(templatesByTypeMap)
          .map(([key, entry]) => ({
            id: key,
            label: this.translateService.instant(`COMMUNICATIONS.NOTIFICATION-TEMPLATES-TYPE-${key}`),
            templates: entry.map(({ uId, name, templateGroupUId, templateGroupName }) => ({
              uId,
              name,
              badgeText: !!templateGroupUId ? templateGroupName : null
            }))
          }))
          .sort((a, b) => Number(a.id) - Number(b.id));
    }
  });

  protected isTakingScreenshot = false;

  constructor(
    private store: Store<fromComm.State>,
    private translateService: TranslateService,
    private cultureService: CultureService,
    private commApiService: CommunicationsApiService,
    private workflowApiService: WorkflowApiService,
    private templateDesignerService: TemplateDesignerService,
    private editorService: QuillEditorService,
    private userProfileApiService: UserProfileApiService,
    private userContextService: UserContextService,
    private channelSelectionService: ChannelSelectionService,
    private screenshotService: ScreenshotService,
    private datasetApiService: DatasetApiService,
    private dataApiService: DataApiService,
    private changeDetector: ChangeDetectorRef,
    private datasetService: DatasetService,
    private notificationStatusService: NotificationStatusService,
    private recipientSelectionService: RecipientSelectionService
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    this.tabs.push(this.translateService.instant('COMMUNICATIONS.TAB-SETTINGS'));
    this.tabs.push(this.translateService.instant('COMMUNICATIONS.TAB-MESSAGE-PREVIEW'));
    this.tabs.push(this.translateService.instant('COMMUNICATIONS.TAB-RECIPIENTS'));
    this.tabs.push(this.translateService.instant('COMMUNICATIONS.TAB-CHANNELS-CASCADES'));

    this.channelSelectionService.loadAvailableChannels(true);
    this.templateDesignerService.init(true);

    this.languages = this.cultureService
      .getUserProfile()
      ?.culture?.orgCultures?.split(',')
      ?.filter(c => c?.trim().length > 0);

    this.store
      .select(fromComm.selectSelectedMassNotification)
      .pipe(withLatestFrom(this.store.select(fromComm.selectIsMassNotificationEditing)), takeUntil(this.destroy$))
      .subscribe(([notification, isEditing]) => {
        const wasActive = this.isActive;
        this.isActive = !!notification && isEditing == true;
        this.changeDetector.markForCheck();
        if (wasActive !== this.isActive) {
          if (this.isActive === true) {
            this.templates.set([]);
            this.templateGroups.set([]);
            this.userProfile = this.cultureService.getUserProfile();
            this.selectedCulture = this.cultureService.currentLang;

            this.loadTemplates();
            this.notification = cloneDeep(notification);
            this.selectedTemplateUId = null;
            this.initNotificationData();
            if (!!this.notification && this.notification?.uId !== GUID_EMPTY) {
              this.loadExistingNotificationData();
            }
          }
        }

        this.isActiveChanged.emit(this.isActive);

        this.checkSmsDesignerOverLimit();
      });

    this.assetCountsByLayer$.pipe(takeUntil(this.destroy$)).subscribe(assetCountsByLayer => {
      if (this.isActive === true && !!this.selectedTemplate) {
        this.refreshMapContextData();
      }
    });

    this.channelSelectionService.channelsLoadedEvents$.pipe(takeUntil(this.destroy$)).subscribe(event => {
      if (!!this.selectedTemplate) {
        this.loadTemplateData(this.selectedTemplate);
      }
    });

    this.notificationStatusService.loadNotificationRatingScale();
  }

  private initNotificationData(): void {
    this.selectedTabIndex = 0;
    this.disabledTabs.push(1);
    this.disabledTabs.push(2);
    this.disabledTabs.push(3);
    this.selectedCulture = this.cultureService.currentLang;
    this.skipTextUpdate = false;
    this.selectedTemplate = null;
    this.skippedChannels = null;
    this.workflow = null;
    this.designExpanded = true;
    this.canEditMessage = true;
    this.selectedMessage = null;
    this.selectedSMSMessage = null;
    this.smsMessageCharCount = 0; // Initialize this
    this.messageCharCount = 0; // Initialize this too
    this.includeAssetsRecipients = false; // Initialize this
    this.templateFilter.set(null);
    this.templateSelectDisplayType.set(TemplateSelectDisplayType.TemplateType);
    this.canSelectTemplate = true;
    this.hasConfirmation = false;
    this.totalAssetRecipients = 0;
    this.relatedAssetRecipientCount = 0;
    this.needToApplyOverrides = false;
    this.showSendConfirmation = false;
    this.templateDesignerService.updateCount(0, true);
    this.isDirty = false;
    this.selectedSeverity = MassNotificationSeverity.Low;
    if (!!this._document && !!this._document['severity_number']) {
      const severity = parseInt(this._document['severity_number']);
      if (!isNaN(severity)) {
        this.selectedSeverity = this.getSeverityLevel(severity);
      }
    }
    this.hasTranslations = this.datasetService.hasTranslations(this._dataset);
    this.limitByLanguage = false;
    this.ratingScaleLevels = this.notificationStatusService.ratingScale?.levels ?? [];
    this.notificationForTimeline = cloneDeep(this.notification);
  }

  private getSeverityLevel(severityLevel: number): number {
    // temporary mapping before we add support based on RatingScale mapping
    if (severityLevel == 1 || severityLevel == 2) {
      return MassNotificationSeverity.Low;
    }
    var result = MassNotificationSeverity.Moderate;

    severityLevel--;
    if (severityLevel <= MassNotificationSeverity.Extreme && severityLevel >= 0) {
      result = severityLevel;
    }
    return result;
  }

  private loadExistingNotificationData(): void {
    this.isDirty = false;
    this.canSelectTemplate = false;
    this.selectedTemplateUId = this.notification?.notificationTemplateUId;
    this.commApiService.getMassNotificationTemplate(this.selectedTemplateUId).subscribe(template => {
      this.selectedTemplate = template;
      if (this.isIncident) {
        this.removeMapControls();
      }
      this.selectedSeverity = this.notification?.notificationSeverity;
      this.selectedMessage = this.templateDesignerService.getTranslateText(
        this.notification?.messageSettings?.notificationMessage,
        this.cultureService.currentLang
      );
      this.selectedSMSMessage = this.templateDesignerService.getTranslateText(
        this.notification?.messageSettings?.smsNotificationMessage,
        this.cultureService.currentLang
      );

      this.smsMessageCharCount = this.selectedSMSMessage?.trim().length;
      const cleanText = this.getMessageCleanText(this.selectedMessage);
      this.messageCharCount = cleanText?.length;

      if (cleanText?.length > 0 && this.selectedSMSMessage?.trim()?.length > 0) {
        this.singleMessageMode = cleanText?.trim() === this.selectedSMSMessage?.trim();
      }

      if (!!this.selectTemplate) {
        this.disabledTabs = [];
      }

      if (this.isIncident) {
        this.removeMapControls();
      }
      this.hasHTMLMessage = this.selectedTemplate?.communicationTemplates?.some(
        t =>
          t.channelUId !== ChannelUIds.TwilioChannelUId &&
          t.channelUId !== ChannelUIds.HorizonSmsChannelUId &&
          t.channelUId !== ChannelUIds.WhatsAppChannelUId
      );
      this.hasSMSMessage = this.selectedTemplate?.communicationTemplates?.some(
        t =>
          t.channelUId === ChannelUIds.TwilioChannelUId ||
          t.channelUId === ChannelUIds.HorizonSmsChannelUId ||
          t.channelUId === ChannelUIds.WhatsAppChannelUId
      );

      this.hasConfirmation = Object.keys(this.selectedTemplate?.defaultMessageSettings?.confirmationText ?? {}).length > 0;

      this.loadWorkflow(this.selectedTemplate?.massNotificationWorkflowUId);
      if (!!this.notification?.channelsCascadeSettings) {
        this.buildSkippedChannels(this.notification?.channelsCascadeSettings);
        this.notification.channelsCascadeSettings = this.copyTemplateCascadeSettings();
      }

      if (!!this.notification?.datasetUId && !!this.notification?.documentUId) {
        this.loadDatasetAndDocument(this.notification);
      }

      this.notification?.recipientSettings?.datasetRecipients
        ?.filter(d => d.documentCount > 0)
        .forEach(r => {
          if (r.sendToRelatedAssets === true) {
            this.recipientSelectionService.getDatasetDocumentsRelated(r, true).subscribe(res => {
              r.relatedCount = res.totalCount;
              r.relatedCountDisplay = res.limitedCounts === true ? `> ${r.relatedCount}` : r.relatedCount.toString();
              r.relatedDatasetUId = res.targetDatasetUId;
              this.calculateTotalRecipients();
            });
          } else {
            r.relatedCount = null;
          }
        });

      this.limitByLanguage = this.notification?.workflowState?.limitByLanguage === true;
      this.getDistListMembersCount();
      this.calculateTotalRecipients();
      this.calculateTotalChannels();
      this.getCurrentWorkflowStepActions();
      this.getTemplateSubjects();
      this.notificationForTimeline = cloneDeep(this.notification);
      this.notificationName = this.notification.name;
      this.contentTabChanged(0);
    });
  }

  removeMapControls() {
    this.selectedTemplate.communicationTemplates.forEach(temp => {
      temp.viewControls = temp.viewControls.filter(
        vc => vc.controlType !== TemplateViewControlType.Map && vc.controlType !== TemplateViewControlType.SelectedAssets
      );
    });
  }

  setSingleMessageMode(): void {
    this.singleMessageMode = !this.singleMessageMode;
    if (this.singleMessageMode === true) {
      this.generateSmsForSingleMessageMode();
    } else {
      this.smsMessageCharCount = this.selectedSMSMessage.trim()?.length;
    }
    this.checkSmsDesignerOverLimit();
  }

  selectTemplate(uId: string): void {
    if (this.isDirty === true) {
      this.potentialTemplateUId = uId;
      this.showChangeTemplateConfirmation = true;
      return;
    }
    this.selectedTemplateUId = uId;
    this.onTemplateChanged();
  }

  setLimitByLanguage(event: any): void {
    this.limitByLanguage = !this.limitByLanguage;
  }

  onTemplateChanged(): void {
    this.initNotificationData();

    this.disabledTabs = [];

    this.commApiService.getMassNotificationTemplate(this.selectedTemplateUId).subscribe(template => {
      this.loadTemplateData(template);
    });
  }

  private loadTemplateData(template: MassNotificationTemplate): void {
    this.selectedTemplate = cloneDeep(template);
    if (this.isIncident) {
      this.removeMapControls();
    }
    this.loadWorkflow(this.selectedTemplate?.massNotificationWorkflowUId);
    this.channelTypes = this.channelSelectionService.getUniqueChannelTypes(this.selectedTemplate?.channelsCascadeSettings);

    const updatedNotification = cloneDeep(this.notification);

    updatedNotification.channelsCascadeSettings = this.copyTemplateCascadeSettings();
    updatedNotification.messageSettings = this.copyTemplateMessageSettings();
    updatedNotification.recipientSettings = this.copyTemplateRecipientSettings();
    updatedNotification.viewControlOverrides = [];

    this.notification = updatedNotification;

    this.notificationForTimeline = cloneDeep(this.notification);

    this.applySkippedChannels(this.notificationForTimeline.channelsCascadeSettings);
    this.contentTabChanged(0);

    this.hasHTMLMessage = this.selectedTemplate?.communicationTemplates?.some(
      t =>
        t.channelUId !== ChannelUIds.TwilioChannelUId &&
        t.channelUId !== ChannelUIds.HorizonSmsChannelUId &&
        t.channelUId !== ChannelUIds.WhatsAppChannelUId
    );
    this.hasSMSMessage = this.selectedTemplate?.communicationTemplates?.some(
      t =>
        t.channelUId === ChannelUIds.TwilioChannelUId ||
        t.channelUId === ChannelUIds.HorizonSmsChannelUId ||
        t.channelUId === ChannelUIds.WhatsAppChannelUId
    );

    if (this.hasHTMLMessage) {
      this.selectedMessage = this.templateDesignerService.getTranslateText(
        this.notification?.messageSettings?.notificationMessage,
        this.cultureService.currentLang
      );
    } else {
      this.selectedMessage = null;
    }
    if (this.hasSMSMessage) {
      this.selectedSMSMessage = this.templateDesignerService.getTranslateText(
        this.notification?.messageSettings?.smsNotificationMessage,
        this.cultureService.currentLang
      );
    } else {
      this.selectedSMSMessage = null;
    }

    let mainText = this.hasHTMLMessage ? this.getMessageCleanText(this.selectedMessage) : this.selectedSMSMessage;
    mainText = mainText?.trim();

    this.singleMessageMode = true;

    if (mainText?.length > 0 && this.selectedSMSMessage?.trim().length > 0) {
      if (mainText !== this.selectedSMSMessage) {
        this.singleMessageMode = false;
      }
    }

    if (this.singleMessageMode === true && this.hasHTMLMessage) {
      this.generateSmsForSingleMessageMode();
    }

    this.smsMessageCharCount = this.selectedSMSMessage?.trim().length;
    this.messageCharCount = mainText?.trim().length;

    this.hasConfirmation = Object.keys(this.selectedTemplate?.defaultMessageSettings?.confirmationText ?? {}).length > 0;

    this.getDistListMembersCount();
    this.calculateTotalRecipients();
    this.calculateTotalChannels();
    this.getTemplateSubjects();
    this.getCurrentWorkflowStepActions();
    this.refreshMapContextData();
    this.refreshMapScreenshot();
    this.notificationName = this.getNewNotificationName();

    this.changeDetector.markForCheck();

    Array.from(document.getElementsByClassName('modal-card-body')).forEach(el => (el.scrollTop = 0));
  }

  skippedChanged(skipped: string[]): void {
    this.skippedChannels = skipped;
    this.isDirty = true;
    this.calculateTotalChannels();
    this.notificationForTimeline = cloneDeep(this.notification);
    this.applySkippedChannels(this.notificationForTimeline.channelsCascadeSettings);

    this.checkSmsDesignerOverLimit();
    this.contentTabChanged(this.selectedContentTabIndex);
  }

  getIconClass(templateType: MassNotificationType): string {
    if (MassNotificationType.SafetyCheck === templateType) {
      return 'icon icon-arrow-undo';
    } else {
      return 'icon icon-small_arrow-right';
    }
  }

  getConfirmationText(): string {
    return this.templateDesignerService.getTranslateText(
      this.selectedTemplate?.defaultMessageSettings?.confirmationText,
      this.cultureService.currentLang
    );
  }

  getConfirmationButtonText(button: ConfirmationButton): string {
    return this.templateDesignerService.getTranslateText(button.text, this.cultureService.currentLang);
  }

  isEmailTemplate(template: CommunicationTemplate): boolean {
    return template?.channelUId === ChannelUIds.SendGridEmailChannelUId || template?.channelUId === ChannelUIds.TopoEmailChannelUId;
  }

  hasTemplateTitle(template: CommunicationTemplate): boolean {
    return template?.channelUId === ChannelUIds.HorizonMobilePushChannelUId || template?.channelUId === ChannelUIds.GwtsMobileChannelUId;
  }

  hasTemplateSummary(template: CommunicationTemplate): boolean {
    return template?.channelUId === ChannelUIds.HorizonMobilePushChannelUId;
  }

  emailSubjectChanged(template: CommunicationTemplate, event: any): void {
    if (this.skipTextUpdate === true) {
      return;
    }
    const updatedNotification = produce(this.notification, draftState => {
      if (!!!draftState.viewControlOverrides) {
        draftState.viewControlOverrides = [];
      }
      let override = draftState.viewControlOverrides?.find(o => o.templateUId === template.uId && o.controlUId === 'emailSubject');
      if (!!!override) {
        override = {
          templateUId: template.uId,
          controlUId: 'emailSubject'
        };
        draftState.viewControlOverrides.push(override);
      }
      if (!!!override.settings) {
        override.settings = {};
      }
      override.settings[this.selectedCulture] = event;

      if (!!!this.contentTabTemplates[this.selectedContentTabIndex]?.settings) {
        this.contentTabTemplates[this.selectedContentTabIndex].settings = {};
      }
      this.contentTabTemplates[this.selectedContentTabIndex].settings.emailSubject = event;
      this.templateSubjects[template.uId] = event;
    });

    this.notification = updatedNotification;
  }

  templateSummaryChanged(template: CommunicationTemplate, event: any): void {
    if (this.skipTextUpdate === true) {
      return;
    }
    const updatedNotification = produce(this.notification, draftState => {
      if (!!!draftState.viewControlOverrides) {
        draftState.viewControlOverrides = [];
      }
      let override = draftState.viewControlOverrides?.find(o => o.templateUId === template.uId && o.controlUId === 'summary');
      if (!!!override) {
        override = {
          templateUId: template.uId,
          controlUId: 'summary'
        };
        draftState.viewControlOverrides.push(override);
      }
      if (!!!override.settings) {
        override.settings = {};
      }
      override.settings[this.selectedCulture] = event;

      if (!!!this.contentTabTemplates[this.selectedContentTabIndex].settings) {
        this.contentTabTemplates[this.selectedContentTabIndex].settings = {};
      }
      this.contentTabTemplates[this.selectedContentTabIndex].settings.summary = event;
      this.templateSummary[template.uId] = event;
    });

    this.notification = updatedNotification;
  }

  private getCurrentWorkflowStepActions(): void {
    this.buttonWorkflowActions = [];

    let step: WorkflowStep;
    if (this.notification.uId === GUID_EMPTY) {
      step = !!this.workflow?.workflowSteps ? this.workflow?.workflowSteps[0] : null;
    } else {
      step = this.workflow?.workflowSteps?.find(s => s.uId === this.notification?.workflowStepUId);
    }

    // descending order, so that primaryAction will be the last one
    const buttonWorkflowActions = step?.actions?.sort((a, b) =>
      (a?.configuration?.primaryAction === true ? -1 : a?.sortOrder) > (b?.configuration?.primaryAction === true ? -1 : b?.sortOrder)
        ? -1
        : 1
    );

    buttonWorkflowActions?.forEach(a => {
      this.buttonWorkflowActions.push({
        name: a?.label ?? this.workflow?.workflowSteps?.find(s => s.uId === a?.destinationStepUId)?.name,
        destinationStepUId: a.destinationStepUId,
        uId: a.uId
      });
    });
  }

  getActionName(action: WorkflowStepAction): string {
    return action?.label ?? this.workflow?.workflowSteps?.find(s => s.uId === action?.destinationStepUId)?.name;
  }

  selectedAssetRecipientsChanged(settings: {
    includeAssetsRecipients: boolean;
    assetRecipientCountsByLayer: ClosestAssetCountByLayerEx[];
  }): void {
    this.includeAssetsRecipients = settings.includeAssetsRecipients;
    this.applyRecipientCountsByLayerToNotification(settings.includeAssetsRecipients, settings.assetRecipientCountsByLayer);
  }

  assetsElementSettingsChanged(data: AssetsElementSettings): void {
    this.designers?.forEach(designer => {
      const assetsControls = designer?.template?.viewControls?.filter(c => c.controlType === TemplateViewControlType.SelectedAssets);
      assetsControls?.forEach(c => {
        designer.setRuntimeData(c.uId, data);
      });
    });
  }

  totalAssetsCountChanged(assetsCount: number): void {
    this.totalAssetRecipients = assetsCount;
    this.calculateTotalRecipients();
  }

  selectedRecipientsChanged(settings: ToRecipientSettings): void {
    this.isDirty = true;

    var tempNotif = cloneDeep(this.notification);
    if (!!!tempNotif.recipientSettings) {
      tempNotif.recipientSettings = {};
    }
    tempNotif.recipientSettings.recipients = settings.recipients;
    tempNotif.recipientSettings.datasetRecipients = [];
    settings.datasetRecipients
      ?.filter(d => d.documentCount > 0)
      .forEach(r => {
        tempNotif.recipientSettings.datasetRecipients.push(r);
      });
    this.notification = tempNotif;
    this.getDistListMembersCount();
    this.notification?.recipientSettings?.datasetRecipients
      ?.filter(d => d.documentCount > 0)
      .forEach(r => {
        if (r.sendToRelatedAssets === true) {
          this.recipientSelectionService.getDatasetDocumentsRelated(r, true).subscribe(res => {
            r.relatedCount = res.totalCount;
            r.relatedCountDisplay = res.limitedCounts === true ? `> ${r.relatedCount}` : r.relatedCount.toString();
            r.relatedDatasetUId = res.targetDatasetUId;
            this.calculateTotalRecipients();
          });
        } else {
          r.relatedCount = null;
        }
      });
    this.calculateTotalRecipients();
  }

  private getDistListMembersCount(): void {
    this.distMemberContacts = {};
    this.recipientSelectionService.getDistListMembersCount(this.notification.recipientSettings.recipients).subscribe(resp => {
      resp.forEach(r => {
        this.distMemberContacts[r.distributionListUId] = r.count * this.templateDesignerService.getNumberOfContactFields(r.datasetUId);
      });
      this.changeDetector.markForCheck();
      this.calculateTotalRecipients();
    });
  }

  get totalDistListContacts(): number {
    let total = 0;
    if (!!this.distMemberContacts) {
      Object.values(this.distMemberContacts).forEach(v => {
        total += v;
      });
    }
    return total;
  }

  isSafetyCheckType(): boolean {
    return this.selectedTemplate?.type === fromComm.MassNotificationType.SafetyCheck;
  }

  private calculateTotalRecipients(): void {
    this.totalRecipients =
      this.notification?.recipientSettings?.recipients?.filter(r => r.type !== MassRecipientType.DistributionList).length ?? 0;
    this.totalRecipients += this.totalDistListContacts;

    this.notification?.recipientSettings?.datasetRecipients
      ?.filter(d => d.documentCount > 0)
      .forEach(r => {
        this.totalRecipients += r?.documentCount;
        if (!!r.relatedCount) {
          this.totalRecipients += r.relatedCount;
        }
      });

    this.totalAddedRecipients = this.totalRecipients;

    if (this.notification?.recipientSettings?.sendToClosestAssets !== false) {
      this.totalRecipients += this.totalAssetRecipients;
    }

    this.totalRecipients += this.notificationDocumentRecipientCount;

    this.totalRecipients += this.relatedAssetRecipientCount;

    this.changeDetector.markForCheck();
  }

  notificationDocumentRecipientsChanged(settings: NotificationDocumentRecipientSettings): void {
    this.isDirty = true;
    const updatedNotification = produce(this.notification, draftState => {
      if (!!!draftState.recipientSettings) {
        draftState.recipientSettings = {};
      }
      draftState.recipientSettings.notificationDocumentSettings = settings;
    });

    this.notification = updatedNotification;
  }

  notificationDocumentRecipientCountChanged(count: number): void {
    this.notificationDocumentRecipientCount = count;
    this.calculateTotalRecipients();
  }

  selectedRelatedAssetRecipientsChanged(settings: RelatedAssetRecipientSettings): void {
    this.isDirty = true;
    this.notification.recipientSettings.relatedAssetSettings = settings;
  }

  relatedAssetRecipientCountChanged(count: number): void {
    this.relatedAssetRecipientCount = count;
    this.calculateTotalRecipients();
  }

  close() {
    this.store.dispatch(fromComm.setSelectedMassNotification({ notification: null, isDirty: false }));
    this.selectedSeverity = MassNotificationSeverity.Low;
  }

  cancelClick() {
    this.close();
  }

  private refreshMapContextData(): void {
    if (!!this.inMapContext) {
      if (!!this._document) {
        setTimeout(() => {
          this.updateDocumentFieldRuntimeData();
          this.updateDesignerDocumentContext();
          this.changeDetector.markForCheck();
        }, 100);
      }
    } else {
      setTimeout(() => {
        (this.selectedTemplate?.communicationTemplates ?? []).forEach(t => {
          t.viewControls.forEach(c => {
            if (c.controlType === TemplateViewControlType.Map) {
              c.settings = null;
            }
          });
        });

        this.designers?.forEach(designer => {
          const mapControls = designer?.template?.viewControls?.filter(c => c.controlType === TemplateViewControlType.Map);
          mapControls?.forEach(c => {
            designer.setRuntimeData(c.uId, null);
          });
          this.changeDetector.markForCheck();
        });
      }, 1);
    }
  }

  private refreshMapScreenshot(): void {
    if (!!this.inMapContext) {
      if (!!this._document) {
        const hasMapControls = this.selectedTemplate?.communicationTemplates?.some(t =>
          t.viewControls?.some(c => c.controlType === TemplateViewControlType.Map)
        );
        if (hasMapControls) {
          this.requestMapScreenshot();
        }
      }
    }
  }

  private anyChannelNeedsRecipients(notification: MassNotification): boolean {
    const channelsCascadeSettings = notification?.channelsCascadeSettings;
    if (!!!channelsCascadeSettings?.channelNotifications) {
      return false;
    }
    for (var channelNotification of channelsCascadeSettings?.channelNotifications) {
      const configuredChannel = this.channelSelectionService.getConfiguredChannel(channelNotification.configuredChannelUId);
      // slack and msteams are only ones that may not need recipients
      if (
        configuredChannel?.channelUId !== ChannelUIds.SlackChannelUId &&
        configuredChannel?.channelUId !== ChannelUIds.MicrosoftTeamsChannelUId
      ) {
        return true;
      }
      if (configuredChannel?.settings?.sendDirectMessages === true) {
        return true;
      }
    }
    return false;
  }

  saveClick(destinationStepUId?: string) {
    if (this.checkSmsDesignerOverLimit()) {
      this.validationList = [this.translateService.instant('COMMUNICATIONS.SMS-OVER-LIMIT')];
      this.isConfirmWarning = false;
      this.showConfirmValidation = true;
      return;
    }

    this.notificationToSave = cloneDeep(this.notification);
    this.notificationToSave.name = this.notificationName;
    this.notificationToSave.workflowUId = this.selectedTemplate.massNotificationWorkflowUId;
    this.notificationToSave.workflowStepUId = destinationStepUId ?? this.workflow?.workflowSteps[0]?.uId;
    this.notificationToSave.notificationTemplateUId = this.selectedTemplateUId;
    this.notificationToSave.notificationType = this.selectedTemplate.type;
    this.notificationToSave.notificationSeverity = this.selectedSeverity;
    this.notificationToSave.source = MassNotificationItemSource.Manual;

    if (this.singleMessageMode === true && this.hasHTMLMessage) {
      if (!!this.notificationToSave.messageSettings?.notificationMessage) {
        if (!!!this.notificationToSave.messageSettings?.smsNotificationMessage) {
          this.notificationToSave.messageSettings.smsNotificationMessage = {} as TextValueByCulture;
        }
      }

      if (!!this.notificationToSave.messageSettings?.notificationMessage) {
        Object.keys(this.notificationToSave.messageSettings?.notificationMessage).forEach(l => {
          var cleanText = this.getMessageCleanText(this.notificationToSave.messageSettings.notificationMessage[l]);
          this.notificationToSave.messageSettings.smsNotificationMessage[l] = cleanText;
        });
      }
    }

    if (this.limitByLanguage === true) {
      this.notificationToSave.workflowState = {};
      this.notificationToSave.workflowState.limitByLanguage = true;
    }

    this.validationList = [];

    this.applySkippedChannels(this.notificationToSave.channelsCascadeSettings);
    this.calculateTotalChannels();

    if (this.totalChannels == 0) {
      this.validationList.push(this.translateService.instant('COMMUNICATIONS.VALIDATION-ERROR-CHANNEL-REQUIRED'));
    }

    if (!(this.totalRecipients > 0)) {
      const needRecipients = this.anyChannelNeedsRecipients(this.notificationToSave);
      if (needRecipients) {
        this.validationList.push(this.translateService.instant('COMMUNICATIONS.VALIDATION-ERROR-RECIPIENT-REQUIRED'));
      }
    }

    const channelsUsed = this.channelSelectionService.getUniqueChannelUIds(this.notificationToSave.channelsCascadeSettings);
    for (var channelUId of channelsUsed) {
      let commTemplate = this.templateDesignerService.getCommunicationTemplateByChannelUId(this.selectedTemplate, channelUId);

      if (!!!commTemplate || !(commTemplate.viewControls?.length > 0)) {
        const name = this.channelSelectionService.getChannelName(channelUId);
        this.validationList.push(this.translateService.instant('COMMUNICATIONS.VALIDATION-TEMPLATE-ERROR-DESIGN-EMPTY', { name: name }));
      }
    }

    let hasEmailOrPush = false;
    let hasPush = false;
    if (!!this.notificationToSave?.channelsCascadeSettings?.channelNotifications) {
      for (var channelNotification of this.notificationToSave?.channelsCascadeSettings?.channelNotifications) {
        const configuredChannel = this.channelSelectionService.getConfiguredChannel(channelNotification.configuredChannelUId);
        if (
          configuredChannel.channelUId === ChannelUIds.SendGridEmailChannelUId ||
          configuredChannel.channelUId === ChannelUIds.TopoEmailChannelUId ||
          configuredChannel.channelUId === ChannelUIds.HorizonMobilePushChannelUId ||
          configuredChannel.channelUId === ChannelUIds.GwtsMobileChannelUId
        ) {
          if (configuredChannel.channelUId === ChannelUIds.HorizonMobilePushChannelUId) {
            hasPush = true;
          }
          hasEmailOrPush = true;
          break;
        }
      }
    }

    const activeTemplates = this.getActiveTemplates();

    if (hasEmailOrPush === true) {
      for (var commTemplate of activeTemplates) {
        if (
          commTemplate.channelUId === ChannelUIds.SendGridEmailChannelUId ||
          commTemplate.channelUId === ChannelUIds.TopoEmailChannelUId ||
          commTemplate.channelUId === ChannelUIds.HorizonMobilePushChannelUId ||
          commTemplate.channelUId === ChannelUIds.GwtsMobileChannelUId
        ) {
          let subject = this.templateDesignerService.getTranslateText(commTemplate.settings?.emailSubject, this.selectedCulture);
          const overrides = this.notificationToSave.viewControlOverrides?.filter(o => o.templateUId === commTemplate.uId);
          const emailOverride = overrides?.find(o => o.controlUId === 'emailSubject');
          if (!!emailOverride) {
            subject = this.templateDesignerService.getTranslateText(emailOverride?.settings, this.selectedCulture);
          }
          if (!!subject) {
            subject = subject.trim();
          }
          if (!(subject?.length > 0)) {
            if (
              commTemplate.channelUId === ChannelUIds.SendGridEmailChannelUId ||
              commTemplate.channelUId === ChannelUIds.TopoEmailChannelUId
            ) {
              this.validationList.push(this.translateService.instant('COMMUNICATIONS.VALIDATION-ERROR-EMAIL-SUBJECT-REQUIRED'));
            } else {
              this.validationList.push(this.translateService.instant('COMMUNICATIONS.VALIDATION-ERROR-TITLE-REQUIRED'));
            }
            break;
          }
        }
      }
    }

    if (hasPush === true) {
      for (var commTemplate of activeTemplates) {
        if (commTemplate.channelUId === ChannelUIds.HorizonMobilePushChannelUId) {
          let summary = this.templateDesignerService.getTranslateText(commTemplate.settings?.summary, this.selectedCulture);
          const overrides = this.notificationToSave.viewControlOverrides?.filter(o => o.templateUId === commTemplate.uId);
          const summaryOverride = overrides?.find(o => o.controlUId === 'summary');
          if (!!summaryOverride) {
            summary = this.templateDesignerService.getTranslateText(summaryOverride?.settings, this.selectedCulture);
          }
          if (!!summary) {
            summary = summary.trim();
          }
          if (!(summary?.length > 0)) {
            this.validationList.push(this.translateService.instant('COMMUNICATIONS.VALIDATION-ERROR-SUMMARY-REQUIRED'));
            break;
          }
        }
      }
    }

    const targetStepType = this.workflow?.workflowSteps?.find(s => s.uId === this.notificationToSave.workflowStepUId)?.workflowStepType;

    if (this.validationList.length > 0) {
      this.isConfirmWarning = targetStepType !== WorkflowStepTypes.Send;
      this.showConfirmValidation = true;
      return;
    }

    if (targetStepType === WorkflowStepTypes.Send) {
      this.buildSendMessages();
      this.showSendConfirmation = true;
    } else {
      this.continueSave();
    }
  }

  private buildSendMessages(): void {
    this.sendConfirmationTitle = this.translateService.instant('COMMUNICATIONS.CONFIRM-SEND-RECIPIENTS', {
      recipients: this.totalRecipients
    });

    this.channelMessageList = [];

    if (!!this.notificationToSave?.channelsCascadeSettings?.channelNotifications?.length) {
      if (this.notificationToSave?.channelsCascadeSettings?.advancedMode === true) {
        this.notificationToSave?.channelsCascadeSettings?.channelNotifications?.forEach(c => {
          const configuredChannel = this.channelSelectionService.getConfiguredChannel(c.configuredChannelUId);
          if (!!configuredChannel) {
            let lineMessage = '';
            if (c.resendTimeMinutes > 0) {
              lineMessage = this.translateService.instant('COMMUNICATIONS.CONFIRM-SEND-INDIVIDUAL-CHANNEL', {
                name: configuredChannel.name,
                resent_number: c.resendTimeMinutes,
                stop_number: c.stopTimeMinutes
              });
            } else {
              lineMessage = this.translateService.instant('COMMUNICATIONS.CONFIRM-SEND-INDIVIDUAL-CHANNEL-SIMPLE', {
                name: configuredChannel.name
              });
            }
            this.channelMessageList.push(lineMessage);
          }
        });

        this.sendConfirmationMessage = this.translateService.instant('COMMUNICATIONS.CONFIRM-SEND-VERIFY');
      } else {
        if (
          !!this.notificationToSave?.channelsCascadeSettings?.channelNotifications?.length &&
          this.notificationToSave?.channelsCascadeSettings?.channelNotifications?.[0].resendTimeMinutes > 0
        ) {
          this.sendConfirmationMessage = this.translateService.instant('COMMUNICATIONS.CONFIRM-SEND-DETAILS-ADVANCED', {
            channels: this.notificationToSave?.channelsCascadeSettings.channelNotifications?.length,
            resent_number: this.notificationToSave?.channelsCascadeSettings?.channelNotifications?.[0].resendTimeMinutes,
            stop_number: this.notificationToSave?.channelsCascadeSettings?.channelNotifications?.[0].stopTimeMinutes
          });
        } else {
          this.sendConfirmationMessage = this.translateService.instant('COMMUNICATIONS.CONFIRM-SEND-DETAILS', {
            channels: this.notificationToSave?.channelsCascadeSettings.channelNotifications?.length
          });
        }
      }
    }
  }

  getActiveNotification(): MassNotification {
    const notificationToShow = cloneDeep(this.notification);
    this.applySkippedChannels(notificationToShow);

    return notificationToShow;
  }

  private continueSave(): void {
    this.applySkippedChannels(this.notificationToSave.channelsCascadeSettings);

    this.notificationToSave.toClosestAssetsSelectionFilter = null;

    if (!!this.closestAssetRequest) {
      this.notificationToSave.toClosestAssetsSelectionFilter = {
        ...this.closestAssetRequest,
        returnedDocumentLimit: null,
        pagination: null
      };
      // include only the visible layers
      this.notificationToSave.toClosestAssetsSelectionFilter.layerStates = [];
      this.closestAssetRequest?.layerStates.forEach(l => {
        if (
          this.notification.recipientSettings.sendToClosestAssetsSettings?.some(
            a => (!!!a.layerUId || a.layerUId === l.layerUId) && a.datasetUId === l.datasetUId
          )
        ) {
          this.notificationToSave.toClosestAssetsSelectionFilter.layerStates.push(l);
        }
      });
    }

    if (!!this._document && !!this._dataset) {
      this.notificationToSave.datasetUId = this._dataset.uId;
      this.notificationToSave.documentUId = this._document.uid;
      this.notificationToSave.mapUId = this.mapUId;
    }

    let successMessage = null;

    const targetStepType = this.workflow?.workflowSteps?.find(s => s.uId === this.notificationToSave.workflowStepUId)?.workflowStepType;
    if (targetStepType === WorkflowStepTypes.Send) {
      successMessage = this.translateService.instant('COMMUNICATIONS.COMMUNICATION-SENT');
    } else if (targetStepType === WorkflowStepTypes.Complete) {
      successMessage = this.translateService.instant('COMMUNICATIONS.COMMUNICATION-COMPLETED');
    } else if (this.notificationToSave.workflowStepUId === this.workflow?.workflowSteps[0]?.uId) {
      successMessage = this.translateService.instant('COMMUNICATIONS.COMMUNICATION-SAVED');
    }

    this.store.dispatch(fromComm.setSelectedMassNotification({ notification: this.notificationToSave, isDirty: true }));
    this.store.dispatch(
      fromComm.saveSelectedMassNotification({ close: true, monitor: !!this.notificationToSave.workflowStepUId, successMessage })
    );
    this.close();
  }

  private getTemplateSubjects(): void {
    this.skipTextUpdate = true;
    this.documentMappings = {};
    if (!!this._dataset && !!this._document) {
      this.documentMappings = this.templateDesignerService.buildDocumentFieldMapping(this._dataset, this._document, this.selectedCulture);
    }
    this.templateSubjects = {};
    this.selectedTemplate?.communicationTemplates?.forEach(t => {
      let subjectValue = this.templateDesignerService.getTranslateText(t?.settings?.emailSubject, this.selectedCulture);

      let override = this.notification.viewControlOverrides?.find(o => o.templateUId === t.uId && o.controlUId === 'emailSubject');
      if (!!override?.settings) {
        subjectValue = this.templateDesignerService.getTranslateText(override.settings, this.selectedCulture);
      }

      if (!!subjectValue) {
        if (!!this._document) {
          Object.keys(this._document).forEach(key => {
            const replacement = '{{' + key + '}}';
            if (!!this.documentMappings && !!this.documentMappings[key]) {
              subjectValue = subjectValue.replace(replacement, this.documentMappings[key].value);
            } else {
              subjectValue = subjectValue.replace(replacement, '');
            }
          });
        }
        subjectValue = subjectValue.replace(/{{\w+}}/g, '');
        this.templateSubjects[t.uId] = subjectValue;
      }
    });
    this.templateSummary = {};
    this.selectedTemplate?.communicationTemplates?.forEach(t => {
      let summaryValue = this.templateDesignerService.getTranslateText(t?.settings?.summary, this.selectedCulture);

      let override = this.notification.viewControlOverrides?.find(o => o.templateUId === t.uId && o.controlUId === 'summary');
      if (!!override?.settings) {
        summaryValue = this.templateDesignerService.getTranslateText(override.settings, this.selectedCulture);
      }

      if (!!summaryValue) {
        if (!!this._document) {
          Object.keys(this._document).forEach(key => {
            const replacement = '{{' + key + '}}';
            if (!!this.documentMappings && !!this.documentMappings[key]) {
              summaryValue = summaryValue.replace(replacement, this.documentMappings[key].value);
            } else {
              summaryValue = summaryValue.replace(replacement, '');
            }
          });
        }
        summaryValue = summaryValue.replace(/{{\w+}}/g, '');
        this.templateSummary[t.uId] = summaryValue;
      }
    });
    this.skipTextUpdate = false;
    this.changeDetector.markForCheck();
  }

  private calculateTotalChannels(): void {
    this.totalChannels = 0;

    this.hasChannels = this.selectedTemplate?.channelsCascadeSettings?.channelNotifications?.length > 0;

    const channelsCascadeSettings = cloneDeep(this.notification?.channelsCascadeSettings);

    this.applySkippedChannels(channelsCascadeSettings);

    this.totalChannels = channelsCascadeSettings?.channelNotifications?.length;
  }

  private applySkippedChannels(channelsCascadeSettings: ChannelsCascadeSettings): void {
    if (!!this.skippedChannels) {
      this.skippedChannels.forEach(s => {
        var index = channelsCascadeSettings.channelNotifications?.findIndex(c => c.uId === s);
        if (index >= 0) {
          channelsCascadeSettings.channelNotifications?.splice(index, 1);
        }
      });
    }

    this.availableChannelUIds = [];

    const allChannelUIds =
      channelsCascadeSettings.channelNotifications?.map(
        n => this.channelSelectionService.availableChannels?.find(c => c.uId === n.configuredChannelUId)?.channelUId
      ) ?? [];

    this.availableChannelUIds = this.channelSelectionService.getChannelUIdsForTemplates(allChannelUIds);

    const activeTemplates = this.getActiveTemplates();
    this.contentTabs = [];
    this.contentTabIcons = [];
    this.contentTabTemplates = [];

    activeTemplates.forEach(t => {
      this.contentTabs.push(this.channelSelectionService.getChannelName(t.channelUId));
      this.contentTabIcons.push(this.channelSelectionService.getChannelIcon(t.channelUId));
      this.contentTabTemplates.push(t);
    });
  }

  get isSmsTab(): boolean {
    if (!this.contentTemplate) return false;
    return (
      this.contentTemplate.channelUId === ChannelUIds.TwilioChannelUId ||
      this.contentTemplate.channelUId === ChannelUIds.HorizonSmsChannelUId
    );
  }

  contentTabChanged(index: number): void {
    if (this.contentTabTemplates?.length > index) {
      this.selectedContentTabIndex = index;
      this.contentTemplate = this.contentTabTemplates[index];
      this.refreshMapContextData();
    } else {
      this.contentTemplate = null;
    }
  }

  private buildSkippedChannels(channelsCascadeSettings: ChannelsCascadeSettings): void {
    this.skippedChannels = [];

    if (!!!this.selectedTemplate) {
      return;
    }

    this.selectedTemplate?.channelsCascadeSettings?.channelNotifications?.forEach(n => {
      if (!channelsCascadeSettings.channelNotifications?.find(c => c.uId === n.uId)) {
        this.skippedChannels.push(n.uId);
      }
    });
  }

  private copyTemplateCascadeSettings(): ChannelsCascadeSettings {
    const settings = !!this.selectedTemplate?.channelsCascadeSettings ? cloneDeep(this.selectedTemplate.channelsCascadeSettings) : null;

    if (!!settings.channelNotifications) {
      settings.channelNotifications = settings.channelNotifications?.filter(n =>
        this.channelSelectionService.availableChannels?.some(c => c.uId === n.configuredChannelUId)
      );
    }
    return settings;
  }
  private copyTemplateMessageSettings(): MessageSettings {
    return !!this.selectedTemplate?.defaultMessageSettings ? cloneDeep(this.selectedTemplate.defaultMessageSettings) : null;
  }
  private copyTemplateRecipientSettings(): ToRecipientSettings {
    return !!this.selectedTemplate?.recipientSettings ? cloneDeep(this.selectedTemplate.recipientSettings) : null;
  }

  onConfirmValidation(confirm: boolean): void {
    this.showConfirmValidation = false;
    if (this.isConfirmWarning === true && confirm === true) {
      this.continueSave();
    }
  }

  onConfirmTemplateChange(confirm: boolean): void {
    this.showChangeTemplateConfirmation = false;
    if (confirm === true) {
      this.selectedTemplateUId = this.potentialTemplateUId;
      this.onTemplateChanged();
    }
  }

  onConfirmSend(confirm: boolean): void {
    this.showSendConfirmation = false;
    if (confirm === true) {
      this.continueSave();
    }
  }

  expandCollapseDesign(): void {
    this.designExpanded = !this.designExpanded;
    this.selectedCulture = this.cultureService.currentLang;
  }

  private getNewNotificationName(): string {
    return `${moment().format('YYYY-MM-DD HH:mm')} ${this.selectedTemplate?.name}`;
  }

  private loadWorkflow(uId: string): void {
    this.getWorkflow(uId)
      .pipe(take(1))
      .subscribe(data => {
        this.workflow = data;
        this.getCurrentWorkflowStepActions();
      });
  }

  private loadTemplates(): void {
    forkJoin({
      templates: this.getTemplates(),
      templateGroups: this.getTemplateGroups()
    })
      .pipe(take(1))
      .subscribe(({ templates, templateGroups }) => {
        this.templates.set(templates);
        this.templateGroups.set(templateGroups);
        this.changeDetector.markForCheck();
      });
  }

  private getTemplates(): Observable<MassNotificationTemplate[]> {
    const params: QueryParams = {
      skip: 0,
      take: 1000,
      where: `IsActive=true && (Type==1 || Type==2)`,
      sortField: 'Name',
      sortDirection: 'Ascending'
    };
    this.userProfile = this.cultureService.getUserProfile();
    return this.commApiService.getMassNotificationTemplates(params, false, false, this.userProfile?.businessUnitUId).pipe(
      catchError(() => of({ data: [] })),
      map(({ data }) => {
        return data;
      })
    );
  }

  private getTemplateGroups(): Observable<MassNotificationTemplateGroup[]> {
    return this.commApiService
      .getMassNotificationTemplateGroups({
        skip: 0,
        take: 10000,
        where: '',
        sortField: 'Name',
        sortDirection: 'Ascending'
      })
      .pipe(
        catchError(() => of({ data: [] })),
        map(({ data = [] }) => data)
      );
  }

  private getWorkflow(uId: string): Observable<Workflow> {
    return this.workflowApiService.getFullWorkflow(uId).pipe(
      catchError(() => of(null)),
      map(result => {
        return result?.data[0];
      })
    );
  }

  get editorModules() {
    this.editorService.init();
    return {
      toolbar: {
        container: [
          [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
          [{ 'font': [] }],
          [{ 'align': [] }],
          ['blockquote'],
          ['link'],
          [{ 'indent': '-1' }, { 'indent': '+1' }],
          [{ 'color': [] }, { 'background': [] }],
          [{ 'list': 'ordered' }, { 'list': 'bullet' }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ redo: 'redo' }, { undo: 'undo' }]
        ],
        handlers: {
          redo: () => {
            this.editor?.history.redo();
          },
          undo: () => {
            this.editor?.history.undo();
          }
        }
      },
      history: { delay: 2000, maxStack: 500, userOnly: true },
      mention: {
        minChars: 1,
        mentionDenotationChars: ['@'],
        source: (searchTerm, renderList) => {
          this.getUserList(searchTerm, renderList);
        }
      }
    };
  }

  editorCreated(editor: any) {
    this.editor = editor;
  }

  private getMessageCleanText(htmlText: string): string {
    if (!!!htmlText) {
      return null;
    }
    const regex = /(<([^>]+)>)/gi;
    return htmlText
      .replace(/<\/p>/g, ' ')
      .replace(/<\/br>/g, ' ')
      .replace(regex, '')
      .replace(/&nbsp;/g, ' ');
  }

  onEditorContentChanged(event: any) {
    this.isDirty = true;

    var cleanText = this.getMessageCleanText(event.html);

    const updatedNotification = produce(this.notification, draftState => {
      if (!!!draftState.messageSettings) {
        draftState.messageSettings = {
          notificationMessage: {} as TextValueByCulture,
          smsNotificationMessage: {} as TextValueByCulture,
          confirmationText: {} as TextValueByCulture,
          confirmationButtons: []
        };
      }
      if (!!!draftState.messageSettings.notificationMessage) {
        draftState.messageSettings.notificationMessage = {} as TextValueByCulture;
      }
      draftState.messageSettings.notificationMessage[this.selectedCulture] = event.html;

      if (this.singleMessageMode === true) {
        if (!!!draftState.messageSettings.smsNotificationMessage) {
          draftState.messageSettings.smsNotificationMessage = {} as TextValueByCulture;
        }
        draftState.messageSettings.smsNotificationMessage[this.selectedCulture] = cleanText;
        this.selectedSMSMessage = cleanText;
      }
    });

    this.notification = updatedNotification;

    this.messageCharCount = cleanText?.length;
    if (this.singleMessageMode) {
      this.checkSmsDesignerOverLimit(cleanText);
    }
  }

  private generateSmsForSingleMessageMode(): void {
    const updatedNotification = produce(this.notification, draftState => {
      if (!!!draftState.messageSettings) {
        draftState.messageSettings = {
          notificationMessage: {} as TextValueByCulture,
          smsNotificationMessage: {} as TextValueByCulture,
          confirmationText: {} as TextValueByCulture,
          confirmationButtons: []
        };
      }
      if (!!!draftState.messageSettings.notificationMessage) {
        draftState.messageSettings.notificationMessage = {} as TextValueByCulture;
      }
      if (this.singleMessageMode === true) {
        if (!!!draftState.messageSettings.smsNotificationMessage) {
          draftState.messageSettings.smsNotificationMessage = {} as TextValueByCulture;
        }
        if (!!this.notification?.messageSettings?.notificationMessage) {
          const cleanText = this.getMessageCleanText(this.notification?.messageSettings?.notificationMessage[this.selectedCulture]);
          draftState.messageSettings.smsNotificationMessage[this.selectedCulture] = cleanText;
          this.selectedSMSMessage = cleanText;
        }
      }
    });

    this.notification = updatedNotification;
  }

  smsNotificationMessageChanged(event: any) {
    this.isDirty = true;

    // Handle both direct value and event object
    const value = typeof event === 'string' ? event : event?.srcElement?.value || event?.target?.value || '';

    const updatedNotification = produce(this.notification, draftState => {
      if (!!!draftState.messageSettings) {
        draftState.messageSettings = {
          notificationMessage: {} as TextValueByCulture,
          smsNotificationMessage: {} as TextValueByCulture,
          confirmationText: {} as TextValueByCulture,
          confirmationButtons: []
        };
      }
      if (!!!draftState.messageSettings.notificationMessage) {
        draftState.messageSettings.notificationMessage = {} as TextValueByCulture;
      }
      if (!!!draftState.messageSettings.smsNotificationMessage) {
        draftState.messageSettings.smsNotificationMessage = {} as TextValueByCulture;
      }
      draftState.messageSettings.smsNotificationMessage[this.selectedCulture] = value;
    });

    this.notification = updatedNotification;
    this.selectedSMSMessage = value;
    this.smsMessageCharCount = this.selectedSMSMessage?.trim().length;

    this.checkSmsDesignerOverLimit(value);
  }

  // Add the missing method that's referenced in the HTML template
  smsNotificationMessageUpdated(event: any) {
    if (this.selectedSMSMessage) {
      this.smsMessageCharCount = this.selectedSMSMessage.trim().length;
    }
  }

  private getUserList(searchText, renderList) {
    this.userProfileApiService.getUsersForMention(this.userContextService.getUserContext().organizationUId, searchText).subscribe(res => {
      const entries = res.data.map(p => ({ id: p.uId, value: `${p.displayName}` }));
      renderList(entries);
      this.changeDetector.markForCheck();
    });
  }

  getActiveTemplates(): CommunicationTemplate[] {
    if (!!this.availableChannelUIds) {
      return this.selectedTemplate?.communicationTemplates?.filter(t => this.availableChannelUIds.includes(t.channelUId));
    } else {
      return this.selectedTemplate?.communicationTemplates;
    }
  }

  templateDesignControlEdited(event: SelectedElementEvent): void {}

  templateDesignControlDeleted(event: SelectedElementEvent): void {
    const updatedNotification = produce(this.notification, draftState => {
      if (!draftState.viewControlOverrides) {
        draftState.viewControlOverrides = [];
      }
      let override = draftState.viewControlOverrides?.find(o => o.templateUId === event.template.uId && o.controlUId === event.control.uId);
      if (!!!override) {
        override = {
          templateUId: event.template.uId,
          controlUId: event.control.uId,
          deleted: true
        };
        draftState.viewControlOverrides.push(override);
      } else {
        override.deleted = true;
      }

      const controlIndex = this.contentTabTemplates[this.selectedContentTabIndex]?.viewControls?.findIndex(
        c => c.uId === event.control.uId
      );
      if (controlIndex >= 0) {
        this.contentTabTemplates[this.selectedContentTabIndex]?.viewControls.splice(controlIndex, 1);
      }
    });

    this.notification = updatedNotification;
    this.isDirty = true;
  }

  templateDesignControlChanged(event: { template: CommunicationTemplate; control: CommunicationTemplateViewControl; settings: any }): void {
    this.isDirty = true;

    const updatedNotification = produce(this.notification, draftState => {
      if (!draftState.viewControlOverrides) {
        draftState.viewControlOverrides = [];
      }

      let override = draftState.viewControlOverrides?.find(o => o.templateUId === event.template.uId && o.controlUId === event.control.uId);
      if (!!!override) {
        override = {
          templateUId: event.template.uId,
          controlUId: event.control.uId
        };
        draftState.viewControlOverrides.push(override);
      }
      override.settings = cloneDeep(event.settings);
    });

    this.notification = updatedNotification;
    this.checkSmsDesignerOverLimit();
  }

  templateDesignChanged(template: CommunicationTemplate): void {
    const tempTemplate = cloneDeep(this.selectedTemplate);
    let commTemplate = tempTemplate.communicationTemplates?.find(c => c.uId === template.uId);
    if (!!commTemplate) {
      commTemplate.settings = template.settings;
      commTemplate.viewControls = template.viewControls;

      const culture = this.selectedCulture || 'en-US';
      commTemplate.viewControls.forEach(ctl => {
        if (ctl.controlType === TemplateViewControlType.Text) {
          const override = this.notification?.viewControlOverrides?.find(
            t => t.templateUId === commTemplate.uId && t.controlUId === ctl.uId
          );
          if (!!override) {
            const messageText = this.templateDesignerService.getTranslateText(override.settings?.richText, culture);
            if (!!messageText) {
              ctl.settings = override.settings;
            }
          }
        }
      });
    }
    this.selectedTemplate = tempTemplate;
  }

  onManageAsset(asset: LayerAssetSummary): void {}

  getAssetName(asset: DocumentSummaryByLayer): string {
    return asset.layerUId;
  }

  getAssetIcon(asset: DocumentSummaryByLayer): string {
    return null;
  }

  private updateDocumentFieldRuntimeData(): void {
    this.designers?.forEach(designer => {
      const fieldControls = designer?.template?.viewControls?.filter(c => c.controlType === TemplateViewControlType.FormField);
      fieldControls?.forEach(c => {
        designer.setRuntimeData(c.uId, this._document);
      });

      const assetsControls = designer?.template?.viewControls?.filter(c => c.controlType === TemplateViewControlType.SelectedAssets);

      if (assetsControls?.length > 0) {
        const items = this.assetCountsByLayer$.value.map(({ count, layer }) => ({
          count,
          name: layer.name
        }));

        assetsControls.forEach(c => {
          designer.setRuntimeData(c.uId, { items });
        });
      }
    });
  }

  private updateDesignerDocumentContext(): void {
    this.designers?.forEach(designer => {
      designer.setDocumentContext(this._dataset, this._document, this.mapUId);
    });
  }

  private requestMapScreenshot(): void {
    const mapElement = document.querySelector('.map-container') as HTMLElement;

    // hide the map zoom elements when taking screenshot
    const gmControlElm = mapElement?.querySelector('.gm-bundled-control');
    gmControlElm?.classList?.add('screenshot-hidden');

    this.isTakingScreenshot = true;

    const imageContent = this.screenshotService
      .takeScreenshot(mapElement)
      .pipe(take(1))
      .subscribe(imageContent => {
        this.mapImage = imageContent;
        gmControlElm?.classList?.remove('screenshot-hidden');
        this.updateDesignMapRuntimeData();
        this.isTakingScreenshot = false;
        this.changeDetector.markForCheck();
      });
  }

  private updateDesignMapRuntimeData(): void {
    const settings: Partial<MapElementSettings> = { imageUrl: this.mapImage };

    const updatedNotification = cloneDeep({
      ...this.notification,
      viewControlOverrides: this.notification?.viewControlOverrides ?? []
    });

    const { viewControlOverrides } = updatedNotification;

    (this.selectedTemplate?.communicationTemplates ?? []).forEach(t => {
      t.viewControls.forEach(c => {
        if (c.controlType === TemplateViewControlType.Map) {
          // set the imageUrl on template control level for displaying channel template preview
          c.settings = {
            ...c.settings,
            ...settings
          };

          // update or add override entry for each channel's template to display in notification
          const override = viewControlOverrides.find(o => o.templateUId === t.uId && o.controlUId === c.uId);

          if (override) {
            override.settings = {
              ...override.settings,
              ...settings
            };
          } else {
            viewControlOverrides.push({
              templateUId: t.uId,
              controlUId: c.uId,
              settings
            });
          }
        }
      });
    });

    this.notification = updatedNotification;

    this.changeDetector.markForCheck();
  }

  private applyRecipientCountsByLayerToNotification(
    includeAssetsRecipients: boolean,
    assetRecipientCountsByLayer: ClosestAssetCountByLayerEx[]
  ): void {
    var tempNotif = cloneDeep(this.notification);

    if (!!!tempNotif.recipientSettings) {
      tempNotif.recipientSettings = {};
    }

    tempNotif.recipientSettings.sendToClosestAssetsSettings = [];
    tempNotif.recipientSettings.sendToClosestAssets = includeAssetsRecipients;

    assetRecipientCountsByLayer.forEach(a => {
      let settings = tempNotif.recipientSettings.sendToClosestAssetsSettings.find(
        s => (!!!s.layerUId || s.layerUId === a.layer.uId) && s.datasetUId === a.layer.datasetUId
      );
      if (!!!settings) {
        settings = {
          datasetUId: a.layer.datasetUId,
          layerUId: a.layer.uId
        };
        tempNotif.recipientSettings.sendToClosestAssetsSettings.push(settings);
      }
      settings.exclude = !a.include;
      settings.sendToRelatedAssets = false;
      if (a.include === true) {
        settings.selectedDocumentsInfo = {
          selectAll: a.selectInfo?.selectAll,
          documentUIds: a.selectInfo?.documentUIds
        };
      }

      settings.sendToRefRelatedAssets = [];
      a.relatedInfoItems.forEach(r => {
        if (r.include === true) {
          const relatedInfo = {
            fieldName: r.fieldName,
            selectedDocumentsInfo: {
              selectAll: r.hasDocumentFilter === true || r.selectInfo?.selectAll,
              documentUIds: r.hasDocumentFilter === true ? [] : r.selectInfo?.documentUIds
            }
          };
          settings.sendToRefRelatedAssets.push(relatedInfo);
        }
      });
    });

    this.notification = tempNotif;
  }

  getLanguageDisplay(lang: string) {
    if (!!lang) {
      return this.translateService.instant(`LANGUAGES.LANGUAGE-${lang}`);
    }
    return '';
  }

  cultureChanged(): void {
    this.getTemplateSubjects();

    if (this.hasHTMLMessage) {
      this.selectedMessage = this.templateDesignerService.getTranslateText(
        this.notification?.messageSettings?.notificationMessage,
        this.selectedCulture
      );
    }
    if (this.hasSMSMessage) {
      this.selectedSMSMessage = this.templateDesignerService.getTranslateText(
        this.notification?.messageSettings?.smsNotificationMessage,
        this.selectedCulture
      );
    }

    this.changeDetector.markForCheck();
  }

  private loadDatasetAndDocument(notification: MassNotification): void {
    this.dataset = null;
    this.document = null;
    this.hasTranslations = false;
    if (!!notification?.datasetUId && !!notification?.documentUId) {
      this.datasetApiService.getDataset(notification?.datasetUId, { includeWorkflow: true }).subscribe(dataset => {
        this._dataset = dataset;

        this.hasTranslations = this.datasetService.hasTranslations(dataset);
        this.dataApiService.getDocument(dataset.uId, notification?.documentUId).subscribe(doc => {
          this._document = doc;

          this.documentContext = { dataset: this._dataset, document: this._document };
        });
      });
    }
  }

  selectSeverity(severity: MassNotificationSeverity): void {
    this.selectedSeverity = severity;
  }

  tabChanged(index: number): void {
    let body = document.querySelector('.modal-card-body');
    if (!!body) {
      body.scrollTop = 0;
    }

    this.selectedTabIndex = index;

    // check if selected content tab is still there
    if (this.selectedTabIndex === 1) {
      if (this.selectedContentTabIndex >= this.contentTabs.length) {
        this.selectedContentTabIndex = 0;
      }
      this.contentTabChanged(this.selectedContentTabIndex);
    }
    this.changeDetector.detectChanges();
  }

  getSmsTabIndex(): number {
    const smsLabel = this.translateService.instant('COMMUNICATIONS.TAB-SMS-TYPE');
    return this.contentTabs.findIndex(tab => tab === smsLabel);
  }

  showAdvacedSchedules(): void {
    this.selectedTabIndex = 3;
  }
  manageRecipients(): void {
    this.selectedTabIndex = 2;
  }

  // Update progress functions to use this.currentCount
  getDesignerProgressValue(): number {
    const ratio = this.designerSmsCount / this.templateDesignerService.MAX_CHARS;
    return Math.min(ratio * 100, 100);
  }

  getDesignerProgressColor(): string {
    return this.checkSmsDesignerOverLimit() ? 'warn' : this.isDesignerApproachingLimit() ? 'accent' : 'primary';
  }

  isDesignerApproachingLimit(): boolean {
    return this.designerSmsCount >= this.warningThreshold && this.designerSmsCount <= this.templateDesignerService.MAX_CHARS;
  }

  checkSmsDesignerOverLimit(smsText?: string): boolean {
    const smsTemplate = this.contentTabTemplates.find(
      t => t.channelUId === ChannelUIds.HorizonSmsChannelUId || t.channelUId === ChannelUIds.TwilioChannelUId
    );
    let designerCount = 0;
    const culture = this.selectedCulture || 'en-US';

    if (!!smsTemplate) {
      smsTemplate.viewControls.forEach(ctl => {
        if (ctl.controlType === TemplateViewControlType.NotificationMessage) {
          if (!!smsText) {
            designerCount += smsText.trim().length;
          } else {
            const messageText = this.templateDesignerService.getTranslateText(
              this.notification?.messageSettings?.smsNotificationMessage,
              culture
            );
            if (!!messageText) {
              designerCount += messageText.trim().length;
            }
          }
        } else if (ctl.controlType === TemplateViewControlType.Text) {
          const override = this.notification?.viewControlOverrides?.find(
            t => t.templateUId === smsTemplate.uId && t.controlUId === ctl.uId
          );
          if (!!override) {
            const messageText = this.templateDesignerService.getTranslateText(override.settings?.richText, culture);
            if (!!messageText) {
              designerCount += messageText.trim().length;
            }
          } else {
            const messageText = this.templateDesignerService.getTranslateText(ctl.settings?.richText, culture);
            if (!!messageText) {
              designerCount += messageText.trim().length;
            }
          }
        } else if (ctl.controlType === TemplateViewControlType.ConfirmationMessage) {
          // Confirmation text
          const confirmationText = this.templateDesignerService.getTranslateText(
            smsTemplate.massNotificationDefaultSettings?.confirmationText,
            culture
          );
          if (!!confirmationText) {
            designerCount += confirmationText.trim().length;
          }
          // Confirmation buttons
          const settings = ctl?.settings as ConfirmationElementSettings;
          const displayAsLink = settings.displayAsLink === true;
          if (displayAsLink === true) {
            const displayText = `${this.translateService.instant('COMMUNICATIONS.MESSAGE-REPLY')} : https://topo.ai/confirmation`;
            designerCount += displayText.trim().length;
          } else {
            const buttons = smsTemplate.massNotificationDefaultSettings?.confirmationButtons || [];
            buttons.forEach((btn: any, ind: any) => {
              const btnText = this.templateDesignerService.getTranslateText(btn.text, culture);
              if (!!btnText) {
                const displayText = `${this.translateService.instant('COMMUNICATIONS.MESSAGE-REPLY')} ${ind + 1}: ${btnText}`;
                designerCount += displayText.trim().length;
              }
            });
          }
        }
      });
    }

    this.designerSmsCount = designerCount;
    this.isDesignerSmsOverLimitReached = designerCount > this.templateDesignerService.MAX_CHARS;
    const isOverLimit = designerCount > this.templateDesignerService.MAX_CHARS;
    this.isDesignerSmsOverLimitReached$.next(isOverLimit);
    this.changeDetector.markForCheck();
    return isOverLimit;
  }

  getDesignerRemainingCharacters(): number {
    return Math.abs(this.designerSmsCount - this.templateDesignerService.MAX_CHARS);
  }

  preventInputWhenLimitReached(event: KeyboardEvent): boolean {
    if (!this.blockInput) {
      return true;
    }

    // Ensure smsMessageCharCount is a valid number
    const currentCount = this.smsMessageCharCount || 0;

    if (currentCount < this.absoluteMaxLimit) {
      return true;
    }

    const allowedKeys = [
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End',
      'Tab',
      'Control',
      'Shift',
      'Alt',
      'Meta'
    ];

    if (allowedKeys.includes(event.key) || event.ctrlKey || event.metaKey) {
      return true;
    }

    if (currentCount >= this.absoluteMaxLimit) {
      event.preventDefault();
      return false;
    }

    return true;
  }

  getRemainingCharacters(): number {
    let remaining: number;
    if (this.isOverLimit()) {
      remaining = this.smsMessageCharCount - this.singleSmsLimit;
    } else {
      remaining = this.singleSmsLimit - this.smsMessageCharCount;
    }
    return isNaN(remaining) ? this.singleSmsLimit : remaining;
  }

  isApproachingLimit(): boolean {
    return this.smsMessageCharCount >= this.warningThreshold && this.smsMessageCharCount < this.singleSmsLimit;
  }

  isOverLimit(): boolean {
    return this.smsMessageCharCount >= this.singleSmsLimit;
  }

  getProgressValue(): number {
    const percentage = (this.smsMessageCharCount / this.singleSmsLimit) * 100;
    return Math.min(percentage, 100);
  }

  getProgressBarColor(): 'primary' | 'accent' | 'warn' {
    if (this.isOverLimit()) {
      return 'warn';
    }
    if (this.isApproachingLimit()) {
      return 'accent';
    }
    return 'primary';
  }
}
